import { createCommandStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/CommandStepItem";
import { createWaitStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/WaitStepItem";
import { createTriggerStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/TriggerStepItem";
import { createInputStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/InputStepItem";
import { createStateHeaderNode } from "~/components/build/Show/components/Sidebar/TreeNode/StateHeaderItem";
import { createSpacerNode } from "~/components/build/Show/components/Sidebar/TreeNode/SpacerItem";
import { createGroupStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/GroupStepItem";
import { createMatrixStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/MatrixStepItem";
import { createParallelStepNode } from "~/components/build/Show/components/Sidebar/TreeNode/ParallelStepItem";

import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import { Step, isMatrixCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import { ReactNode } from "react";
import { Build } from "~/components/build/Show/lib/types";
import {
  FilterByOption,
  StateOrder,
  groupStepsByState,
} from "~/components/build/Show/lib/useFilterStore";

/** @deprecated Use `~/components/build/Show/routes/steps/list/-components/TreeNode` instead. */
export enum TreeNodeType {
  StepCommand = "step_command",
  StepGroup = "step_group",
  StepCommandMatrix = "step_command_matrix",
  StepCommandParallel = "step_command_parallel",
  StepInput = "step_input",
  StepTrigger = "step_trigger",
  StepWait = "step_wait",
  JobCommand = "job_command",
  ShowHiddenJobs = "show_hidden_jobs",
  StateHeader = "state_header",
  Spacer = "spacer",
}

/** @deprecated Use `~/components/build/Show/routes/steps/list/-components/TreeNode` instead. */
export interface TreeNode {
  id: string;
  type: TreeNodeType;
  render: (props: TreeNodeRenderProps) => ReactNode;
  childNodes?: (collapsibleState: CollapsibleState, filters: FilterByOption[]) => TreeNode[];
}

/** @deprecated Use `~/components/build/Show/routes/steps/list/-components/TreeNode` instead. */
export interface TreeNodeRenderProps {
  depth: number;
  collapsibleState: CollapsibleState;
}

/** @deprecated Use `~/components/build/Show/routes/steps/list/-components/TreeNode` instead. */
export function createStepNode(step: Step, build: Build): TreeNode {
  switch (step.type) {
    case "group":
      return createGroupStepNode(step, build);
    case "command":
      if (isParallelCommandStep(step)) {
        return createParallelStepNode(step, build);
      } else if (isMatrixCommandStep(step)) {
        return createMatrixStepNode(step, build);
      }
      return createCommandStepNode(step);
    case "trigger":
      return createTriggerStepNode(step);
    case "wait":
      return createWaitStepNode(step);
    case "block":
    case "input":
      return createInputStepNode(step);
    default:
      throw new Error(`Unknown step type: ${step.type}`);
  }
}

/**
 * Turn a flat list of steps into a list of nodes with child groupings.
 */
/** @deprecated Use `~/components/build/Show/routes/steps/list/-components/TreeNode` instead. */
export function createNodesForSteps(build: Build, steps: Step[]): TreeNode[] {
  const nodes: TreeNode[] = [];

  steps.forEach((step) => {
    if (!step.groupUuid) {
      nodes.push(createStepNode(step, build));
    }
  });

  return nodes;
}

/**
 * Create a tree of nodes grouped by state (ungrouped from parent)
 *
 * ie.
 *    **Failed**
 *      -> Rubocop
 *      -> Rspec
 *        -> Run 1
 *        -> Run 2
 */
/** @deprecated Use `~/components/build/Show/routes/steps/list/-components/TreeNode` instead. */
export function createNodesForStepsGroupedByState(
  build: Build,
  stateCounts: Record<string, number>,
): TreeNode[] {
  const nodes: TreeNode[] = [];

  StateOrder.forEach((state) => {
    if (!stateCounts[state]) {
      return;
    }

    const node = createStateHeaderNode(state, stateCounts[state], build);
    nodes.push(node);
    nodes.push(createSpacerNode(`${state}_spacer`));
  });

  return nodes;
}
